import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Modal from "@cloudscape-design/components/modal"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { type ReactNode, useState } from "react"

import { useNotifications } from "@/contexts/NotificationContext"
import { parseApiErrorMessage } from "@/utils/apiErrors"

interface ConfirmedRevokeOptions<G> {
  /**
   * Revokes a grant; a mutation's `mutate`.
   */
  revoke: (grant: G, callbacks: { onSuccess: () => void; onError: (err: Error) => void }) => void
  /**
   * What is removed from whom, e.g. "DTA Approver from sam@acme.example".
   */
  describe: (grant: G) => string
  /**
   * Success toast text, e.g. "sam@acme.example no longer has DTA Approver".
   */
  revokedMessage: (grant: G) => string
}

/**
 * A revoke that asks for confirmation first and reports the outcome as a toast.
 *
 * @returns `requestRevoke` to open the confirmation for a grant, and the `modal` to render.
 */
export function useConfirmedRevoke<G>({
  revoke,
  describe,
  revokedMessage,
}: ConfirmedRevokeOptions<G>): { requestRevoke: (grant: G) => void; modal: ReactNode } {
  const { showToast } = useNotifications()
  const [pending, setPending] = useState<G | null>(null)

  const confirm = () => {
    if (pending === null) return
    const grant = pending
    setPending(null)
    revoke(grant, {
      onSuccess: () =>
        showToast({
          subject: "Access revoked",
          message: revokedMessage(grant),
          variant: "success",
        }),
      onError: (err) =>
        showToast({
          subject: "Revoke failed",
          message: parseApiErrorMessage(err, "Failed to revoke access"),
          variant: "error",
        }),
    })
  }

  const modal = pending !== null && (
    <Modal
      visible
      onDismiss={() => setPending(null)}
      header="Revoke access"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirm}>
              Revoke
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {`Remove ${describe(pending)}? They lose the access this grant gives.`}
    </Modal>
  )

  return { requestRevoke: setPending, modal }
}
